/**CORE LIBRARIES IMPORT */
import { useEffect, useState } from "react";

/**SERVICES / DATA IMPORT */
import { fetchEventContent, fetchMenuContent } from "../services/content";
import { menuCategories, menuItems } from "../data/menu";
import { getEventBySlug } from "../data/events";
import { isBackendConfigured } from "../services/config";

/** Event content from the portal (Supabase) when available, else the static event. */
export function useEventContent(staticEvent) {
  const [event, setEvent] = useState(staticEvent);
  useEffect(() => {
    setEvent(staticEvent);
    if (!staticEvent) return undefined;
    let cancelled = false;
    fetchEventContent(staticEvent.slug, staticEvent)
      .then((remote) => !cancelled && remote && setEvent(remote))
      .catch(() => {
        /* keep static content */
      });
    return () => {
      cancelled = true;
    };
  }, [staticEvent]);
  return event;
}

/** Menu from the portal (Supabase) when available, else src/data/menu.js. */
export function useMenuContent() {
  const [menu, setMenu] = useState({ categories: menuCategories, items: menuItems });
  useEffect(() => {
    let cancelled = false;
    fetchMenuContent()
      .then((remote) => !cancelled && remote && setMenu(remote))
      .catch(() => {
        /* keep static menu */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return menu;
}

/**
 * Any bookable event by slug: the static event (with portal content merged
 * in) or, when it only exists in the database (e.g. created in the portal),
 * the database event. `loading` is true until the database has answered.
 */
export function useResolvedEvent(slug) {
  const staticEvent = slug ? getEventBySlug(slug) : null;
  const [state, setState] = useState({
    event: staticEvent || null,
    loading: Boolean(slug) && !staticEvent && isBackendConfigured(),
  });
  useEffect(() => {
    let cancelled = false;
    setState({ event: staticEvent || null, loading: Boolean(slug) && !staticEvent && isBackendConfigured() });
    if (!slug) return undefined;
    fetchEventContent(slug, staticEvent || { slug })
      .then((remote) => !cancelled && setState({ event: remote || staticEvent || null, loading: false }))
      .catch(() => !cancelled && setState((st) => ({ ...st, loading: false })));
    return () => {
      cancelled = true;
    };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}
